import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Button, Link, Spacing, Text } from '@reservamos/elements';
import Header from 'components/Header';
import Loading from 'components/Loading';
import { getPurchase } from '@/actions/purchase';
import WhatsappButton from 'components/WhatsappButton';
import DotersPointsSummary from 'ui/atoms/DotersPointsSummary';
import BrandLogo from '../../BrandLogo';
import useReactNativeMessage from '../../../hooks/useReactNativeMessage';
import 'styles/components/payment/FailedPayment';
import PurchaseLayout from '../../../ui/layouts/PurchaseLayout';
import PaymentStatusIcon from '../../purchase/PaymentStatusIcon';
import CompleteHeader from '../../../ui/atoms/CompleteHeader';

const FailedPayment = ({ t }) => {
  const purchase = useSelector((state) => state.purchase.toJS());
  const [expired, setExpired] = useState(false);
  const {
    copies: { provider },
  } = useSelector((state) => state.whitelabelConfig);
  const { phones, email } = provider;
  const history = useHistory();
  const { token } = useParams();
  const dispatch = useDispatch();
  const RNMessage = useReactNativeMessage();
  const canDotersGenerate = Boolean(purchase.walletAccrualPoints || purchase.walletPointsUsed);
  const phoneItems = phones
    .map((phone) => (
      <a href={`tel:${phone.value}`} key={phone.value}>
        {phone.display}
      </a>
    ))
    .reduce((links, link) => (links ? [...links, ', ', link] : [link]), null);

  const retryPayment = () => {
    history.push(`/purchase/${token}/checkout`);
  };

  useEffect(() => {
    // Only for Mobile app webview
    // Verify if payment has succeed
    RNMessage.post({ error: 'Payment has failed' });
    dispatch(getPurchase(token));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (purchase) {
      const expiration = purchase.expiresAt;

      if (!expiration) return;

      const currentTime = moment();
      const expireTime = moment(expiration);
      const remainingTime = expireTime.diff(currentTime, 'seconds', true);

      if (remainingTime <= 0) {
        setExpired(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase]);

  if (!purchase.loaded) return <Loading />;

  return (
    <div className="l-complete">
      <Header isFixed>
        <BrandLogo />
      </Header>

      <CompleteHeader title={t('payment:payment_status_failed')} status="failed" />
      <PurchaseLayout
        sidebar={
          <Spacing size="XL" vertical>
            <div>
              <PaymentStatusIcon />
            </div>
          </Spacing>
        }
      >
        <Spacing vertical size="XL">
          <Text>{t('payment:text.purchase_not_made')}</Text>
          {canDotersGenerate && (
            <DotersPointsSummary dotersUsed={purchase.walletPointsUsed} status="refunded" />
          )}
          <Spacing size="M" isResponsive>
            {!expired && (
              <div>
                <Button
                  onClick={retryPayment}
                  text={t('payment:button.try_the_pay_again')}
                  variant="primary"
                  padding="XL"
                  withHeight
                  fullWidthOnSmall
                />
              </div>
            )}
            <WhatsappButton token={token} />
          </Spacing>
          <Text>
            {t('payment:text.more_information_in')}
            {email && (
              <>
                <span>{` ${t('purchase:text.at')} `}</span>
                <Link href={`mailto:${email}`} text={email} size="M" type="accent" />
              </>
            )}
            {phoneItems && phoneItems.length && (
              <span>
                <span>{email && ` ${t('purchase:text.or')} `}</span>
                {phones.length > 1
                  ? ` ${t('purchase:text.in_the_lines')} `
                  : ` ${t('purchase:text.on_the_line')} `}
                {phoneItems}
              </span>
            )}
          </Text>
          <Text weight="bold">{t('payment:text.happy_to_help_you')}</Text>
        </Spacing>
      </PurchaseLayout>
    </div>
  );
};

FailedPayment.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(FailedPayment);
